/**
 * Smart Mock Data Generator
 * Generates contextual, relevant mock data for prompt variables
 * Analyzes variable names and prompt context to provide meaningful examples
 * Enhanced with comprehensive nonprofit-specific templates and variable options
 */

import mockDataTemplates from '../data/mock-data-templates.json';
import variableOptionsLibrary from '../data/variable-options-library.json';

export interface MockDataConfig {
  mockData: Record<string, string>;
  variableOptions: Record<string, string[]>;
}

export interface CategoryTemplates {
  common_variables: Record<string, string>;
  specific_patterns: Record<string, string[]>;
}

export class MockDataGenerator {
  // Common nonprofit organization names
  private static organizationNames = [
    "Hope Community Center", "United Way", "Salvation Army", "Habitat for Humanity",
    "Boys & Girls Club", "Red Cross", "Goodwill", "YMCA", "Food Bank Network",
    "Children's Defense Fund", "Mercy Corps", "Doctors Without Borders",
    "American Cancer Society", "Make-A-Wish Foundation", "Special Olympics"
  ];

  // Nonprofit-specific data patterns
  private static nonprofitPatterns = {
    // Organization types
    organizationTypes: ["501(c)(3) Nonprofit", "Charity", "Foundation", "NGO", "Community Organization", "Religious Organization"],
    
    // Beneficiary groups
    beneficiaryTypes: ["Youth", "Seniors", "Families", "Veterans", "Students", "Adults", "Children", "Homeless individuals", "Low-income families"],
    
    // Program types
    programTypes: ["Education Support Program", "Food Assistance Program", "Housing Support Initiative", "Youth Development Program", "Senior Care Services", "Job Training Program"],
    
    // Deliverable types
    deliverableTypes: ["Annual Report", "Impact Dashboard", "Grant Proposal", "Strategic Plan", "Community Assessment", "Program Evaluation"],
    
    // Common constraints
    constraints: [
      "Limited funding and staff capacity",
      "Regulatory compliance requirements", 
      "Geographic service area restrictions",
      "Seasonal demand fluctuations",
      "Volunteer recruitment challenges"
    ],

    // Mission statements
    missionStatements: [
      "Empowering underserved communities through education and support services",
      "Building stronger families and safer communities for all",
      "Providing hope and opportunity to those in need",
      "Creating lasting positive change in our community",
      "Supporting vulnerable populations with dignity and respect"
    ],

    // Stakeholder needs
    stakeholderNeeds: [
      "Transparent reporting and measurable outcomes",
      "Cost-effective service delivery",
      "Community engagement and partnership",
      "Sustainable funding strategies",
      "Evidence-based program effectiveness"
    ],

    // Budget ranges
    budgetRanges: ["$25,000", "$50,000", "$75,000", "$100,000", "$250,000", "$500,000"],
    
    // Timeline options
    timelines: ["3 months", "6 months", "12 months", "18 months", "2 years"],

    // Location examples
    locations: ["San Francisco, CA", "New York, NY", "Austin, TX", "Denver, CO", "Atlanta, GA", "Seattle, WA"],

    // KPI metrics
    kpiMetrics: [
      "Number of clients served",
      "Program completion rates", 
      "Community engagement levels",
      "Volunteer retention rates",
      "Cost per beneficiary served"
    ]
  };

  /**
   * Generates contextual mock data for a prompt's variables
   */
  /**
   * Generate select placeholder text
   */
  private static generateSelectPlaceholder(variableName: string): string {
    const formatted = this.formatLabel(variableName);
    return `[Select ${formatted}]`;
  }

  /**
   * Determines the prompt category based on content analysis
   */
  static determinePromptCategory(promptText: string): string {
    const promptLower = promptText.toLowerCase();
    
    // Grant writing indicators
    if (promptLower.includes('grant') || promptLower.includes('funding') || 
        promptLower.includes('proposal') || promptLower.includes('foundation')) {
      return 'grant_writing';
    }
    
    // Fundraising indicators
    if (promptLower.includes('fundrais') || promptLower.includes('donor') || 
        promptLower.includes('campaign') || promptLower.includes('donation')) {
      return 'fundraising';
    }
    
    // Communications indicators
    if (promptLower.includes('communication') || promptLower.includes('messaging') || 
        promptLower.includes('audience') || promptLower.includes('outreach')) {
      return 'communications';
    }
    
    // Program management indicators
    if (promptLower.includes('program') || promptLower.includes('service') || 
        promptLower.includes('participant') || promptLower.includes('management')) {
      return 'program_management';
    }
    
    // Marketing indicators
    if (promptLower.includes('marketing') || promptLower.includes('brand') || 
        promptLower.includes('promotion') || promptLower.includes('awareness')) {
      return 'marketing';
    }
    
    // Data analysis indicators
    if (promptLower.includes('data') || promptLower.includes('analys') || 
        promptLower.includes('metric') || promptLower.includes('evaluation')) {
      return 'data_analysis';
    }
    
    // Research indicators
    if (promptLower.includes('research') || promptLower.includes('study') || 
        promptLower.includes('survey') || promptLower.includes('investigation')) {
      return 'research';
    }
    
    // Brainstorming indicators
    if (promptLower.includes('brainstorm') || promptLower.includes('ideation') || 
        promptLower.includes('creative') || promptLower.includes('innovation')) {
      return 'brainstorming';
    }
    
    // Default to communications for general prompts
    return 'communications';
  }

  static generateMockData(promptText: string, variables: Array<{name: string, type?: string}>): MockDataConfig {
    const mockData: Record<string, string> = {};
    const variableOptions: Record<string, string[]> = {};

    // Determine prompt category for targeted mock data
    const promptCategory = this.determinePromptCategory(promptText);
    const categoryData = mockDataTemplates[promptCategory as keyof typeof mockDataTemplates] as CategoryTemplates;

    // Analyze prompt context for smarter data generation (legacy support)
    const promptLower = promptText.toLowerCase();
    const isGrantWriting = promptCategory === 'grant_writing';
    const isAssessment = promptCategory === 'data_analysis' || promptCategory === 'research';
    const isStrategic = promptLower.includes('strateg') || promptLower.includes('plan') || promptLower.includes('framework');

    variables.forEach((variable) => {
      const varName = variable.name.toUpperCase();
      const varNameLower = variable.name.toLowerCase();

      // Priority 1: Check category-specific templates first
      if (categoryData?.common_variables[varName]) {
        mockData[varName] = categoryData.common_variables[varName];
      }
      
      // Priority 2: Check category-specific patterns
      else if (categoryData?.specific_patterns[varName]) {
        const options = categoryData.specific_patterns[varName];
        mockData[varName] = this.generateSelectPlaceholder(varName);
        variableOptions[varName] = options;
      }
      
      // Priority 3: Check comprehensive variable options library
      else if (this.findVariableInOptionsLibrary(varName)) {
        const libraryOptions = this.findVariableInOptionsLibrary(varName);
        if (libraryOptions) {
          mockData[varName] = this.generateSelectPlaceholder(varName);
          variableOptions[varName] = libraryOptions;
        }
      }

      // Priority 4: Legacy pattern matching (fallback)
      else if (this.matchesPattern(varNameLower, ['organization', 'org', 'nonprofit', 'ngo'])) {
        const orgOptions = variableOptionsLibrary.organizations.ORGANIZATION_NAME;
        mockData[varName] = this.generateSelectPlaceholder(varName);
        variableOptions[varName] = orgOptions.slice(0, 8); // Limit for UI
      }
      
      else if (this.matchesPattern(varNameLower, ['organization_type', 'org_type', 'entity_type'])) {
        mockData[varName] = "501(c)(3) Nonprofit";
        variableOptions[varName] = this.nonprofitPatterns.organizationTypes;
      }
      
      else if (this.matchesPattern(varNameLower, ['beneficiary', 'target', 'audience', 'recipient'])) {
        mockData[varName] = isGrantWriting ? "Low-income families" : "Youth";
        variableOptions[varName] = this.nonprofitPatterns.beneficiaryTypes;
      }
      
      else if (this.matchesPattern(varNameLower, ['program', 'initiative', 'project', 'service'])) {
        mockData[varName] = isGrantWriting ? "Community Outreach Program" : "Education Support Program";
        variableOptions[varName] = this.nonprofitPatterns.programTypes;
      }
      
      else if (this.matchesPattern(varNameLower, ['constraint', 'challenge', 'limitation', 'barrier'])) {
        mockData[varName] = isGrantWriting 
          ? "Limited funding and regulatory compliance requirements"
          : "Limited funding and staff capacity";
        variableOptions[varName] = this.nonprofitPatterns.constraints;
      }
      
      else if (this.matchesPattern(varNameLower, ['mission', 'purpose', 'goal', 'objective'])) {
        mockData[varName] = isStrategic 
          ? "Creating lasting positive change in our community"
          : "Empowering underserved communities through education and support";
        variableOptions[varName] = this.nonprofitPatterns.missionStatements;
      }
      
      else if (this.matchesPattern(varNameLower, ['stakeholder', 'need', 'requirement', 'expectation'])) {
        mockData[varName] = isAssessment 
          ? "Evidence-based program effectiveness and measurable outcomes"
          : "Transparent reporting and community engagement";
        variableOptions[varName] = this.nonprofitPatterns.stakeholderNeeds;
      }
      
      else if (this.matchesPattern(varNameLower, ['deliverable', 'output', 'result', 'outcome'])) {
        mockData[varName] = isGrantWriting ? "Grant Proposal" : "Impact Dashboard";
        variableOptions[varName] = this.nonprofitPatterns.deliverableTypes;
      }
      
      else if (this.matchesPattern(varNameLower, ['budget', 'funding', 'amount', 'allocation', 'resource'])) {
        mockData[varName] = isGrantWriting ? "$250,000" : "$75,000";
        variableOptions[varName] = this.nonprofitPatterns.budgetRanges;
      }
      
      else if (this.matchesPattern(varNameLower, ['timeline', 'duration', 'timeframe', 'period'])) {
        mockData[varName] = isStrategic ? "12 months" : "6 months";
        variableOptions[varName] = this.nonprofitPatterns.timelines;
      }
      
      else if (this.matchesPattern(varNameLower, ['location', 'city', 'area', 'region', 'community'])) {
        mockData[varName] = this.getRandomItem(this.nonprofitPatterns.locations);
        variableOptions[varName] = this.nonprofitPatterns.locations;
      }
      
      else if (this.matchesPattern(varNameLower, ['metric', 'kpi', 'indicator', 'measure'])) {
        mockData[varName] = isAssessment ? "Program completion rates and client satisfaction scores" : "Number of clients served";
        variableOptions[varName] = this.nonprofitPatterns.kpiMetrics;
      }
      
      else if (this.matchesPattern(varNameLower, ['count', 'number', 'quantity', 'participant'])) {
        mockData[varName] = isGrantWriting ? "500" : "150";
        // Numbers don't need options arrays
      }
      
      else if (this.matchesPattern(varNameLower, ['email', 'contact'])) {
        mockData[varName] = "director@hopecommunitycenter.org";
      }
      
      else if (this.matchesPattern(varNameLower, ['phone', 'telephone'])) {
        mockData[varName] = "(555) 123-4567";
      }
      
      else if (this.matchesPattern(varNameLower, ['website', 'url'])) {
        mockData[varName] = "www.hopecommunitycenter.org";
      }
      
      else {
        // Check if this is a date field based on variable name
        if (this.matchesPattern(varNameLower, ['date', 'deadline', 'due', 'start', 'end', 'launch', 'completion'])) {
          // Generate a proper date string in YYYY-MM-DD format
          const futureDate = new Date();
          futureDate.setMonth(futureDate.getMonth() + (isGrantWriting ? 6 : 3));
          mockData[varName] = futureDate.toISOString().split('T')[0];
        } else {
          // Fallback: Generate contextual data based on prompt type
          if (isGrantWriting) {
            mockData[varName] = `Grant-related ${this.formatLabel(varName).toLowerCase()}`;
          } else if (isAssessment) {
            mockData[varName] = `Assessment ${this.formatLabel(varName).toLowerCase()}`;
          } else if (isStrategic) {
            mockData[varName] = `Strategic ${this.formatLabel(varName).toLowerCase()}`;
          } else {
            mockData[varName] = `Sample ${this.formatLabel(varName)}`;
          }
        }
      }
    });

    // Convert fields with options to use "[Select ...]" format for dropdowns
    Object.keys(variableOptions).forEach(varName => {
      if (variableOptions[varName] && variableOptions[varName].length > 1) {
        mockData[varName] = this.generateSelectPlaceholder(varName);
      }
    });

    return { mockData, variableOptions };
  }

  /**
   * Finds variable options in the comprehensive variable options library
   */
  private static findVariableInOptionsLibrary(varName: string): string[] | null {
    // Search through all categories in the variable options library
    for (const categoryKey in variableOptionsLibrary) {
      const category = variableOptionsLibrary[categoryKey as keyof typeof variableOptionsLibrary];
      if (category[varName as keyof typeof category]) {
        return category[varName as keyof typeof category] as string[];
      }
    }
    
    // Check for partial matches with common patterns
    const varNameLower = varName.toLowerCase();
    
    // Organization-related variables
    if (this.matchesPattern(varNameLower, ['organization', 'org']) && !this.matchesPattern(varNameLower, ['type'])) {
      return variableOptionsLibrary.organizations.ORGANIZATION_NAME;
    }
    if (this.matchesPattern(varNameLower, ['organization_type', 'org_type', 'entity_type'])) {
      return variableOptionsLibrary.organizations.ORGANIZATION_TYPE;
    }
    
    // Funding-related variables
    if (this.matchesPattern(varNameLower, ['funder', 'foundation']) && !this.matchesPattern(varNameLower, ['name'])) {
      return variableOptionsLibrary.funding_and_grants.FUNDER;
    }
    if (this.matchesPattern(varNameLower, ['grant_amount', 'funding_amount', 'award_amount'])) {
      return variableOptionsLibrary.funding_and_grants.GRANT_AMOUNT;
    }
    
    // Program-related variables
    if (this.matchesPattern(varNameLower, ['program_type', 'service_type', 'initiative_type'])) {
      return variableOptionsLibrary.programs_and_services.PROGRAM_TYPE;
    }
    if (this.matchesPattern(varNameLower, ['target_population', 'beneficiary_type', 'audience'])) {
      return variableOptionsLibrary.programs_and_services.TARGET_POPULATION;
    }
    
    // Communication-related variables
    if (this.matchesPattern(varNameLower, ['communication_channel', 'channel', 'platform'])) {
      return variableOptionsLibrary.communications_and_marketing.COMMUNICATION_CHANNEL;
    }
    if (this.matchesPattern(varNameLower, ['tone', 'messaging_tone', 'voice'])) {
      return variableOptionsLibrary.communications_and_marketing.MESSAGING_TONE;
    }
    
    return null;
  }

  /**
   * Checks if a variable name matches any of the given patterns
   */
  private static matchesPattern(varName: string, patterns: string[]): boolean {
    return patterns.some(pattern => varName.includes(pattern));
  }

  /**
   * Gets a random item from an array
   */
  private static getRandomItem<T>(array: T[]): T {
    return array[Math.floor(Math.random() * array.length)];
  }

  /**
   * Formats a variable name to a readable label
   */
  private static formatLabel(variableName: string): string {
    return variableName
      .toLowerCase()
      .replace(/_/g, ' ')
      .replace(/\b\w/g, str => str.toUpperCase())
      .trim();
  }

  /**
   * Generates comprehensive mock data for a specific nonprofit category
   * Useful for populating database records with contextual mock data
   */
  static generateCategoryMockData(category: string): MockDataConfig {
    const categoryData = mockDataTemplates[category as keyof typeof mockDataTemplates] as CategoryTemplates;
    
    if (!categoryData) {
      console.warn(`No mock data template found for category: ${category}`);
      return { mockData: {}, variableOptions: {} };
    }

    const mockData: Record<string, string> = {};
    const variableOptions: Record<string, string[]> = {};

    // Add all common variables from the category template
    Object.entries(categoryData.common_variables).forEach(([varName, value]) => {
      mockData[varName] = value;
    });

    // Add all specific patterns as variable options
    Object.entries(categoryData.specific_patterns).forEach(([varName, options]) => {
      variableOptions[varName] = options;
      mockData[varName] = this.generateSelectPlaceholder(varName);
    });

    return { mockData, variableOptions };
  }

  /**
   * Gets all available categories with mock data templates
   */
  static getAvailableCategories(): string[] {
    return Object.keys(mockDataTemplates);
  }

  /**
   * Gets all variables available in the options library for a specific category
   */
  static getVariableOptionsByCategory(category: string): Record<string, string[]> {
    const relevantOptions: Record<string, string[]> = {};
    
    // Map category to relevant variable option categories
    const categoryMappings: Record<string, string[]> = {
      'grant_writing': ['organizations', 'funding_and_grants', 'programs_and_services', 'impact_and_outcomes'],
      'fundraising': ['organizations', 'fundraising_and_development', 'communications_and_marketing'],
      'communications': ['organizations', 'communications_and_marketing', 'geographic_and_demographic'],
      'program_management': ['organizations', 'programs_and_services', 'impact_and_outcomes', 'operational_and_management'],
      'marketing': ['organizations', 'communications_and_marketing', 'geographic_and_demographic'],
      'data_analysis': ['data_and_research', 'impact_and_outcomes', 'operational_and_management'],
      'research': ['data_and_research', 'impact_and_outcomes', 'geographic_and_demographic'],
      'brainstorming': ['organizations', 'programs_and_services', 'operational_and_management', 'time_and_scheduling']
    };

    const relevantCategoryKeys = categoryMappings[category] || [];
    
    relevantCategoryKeys.forEach(catKey => {
      const categoryOptions = variableOptionsLibrary[catKey as keyof typeof variableOptionsLibrary];
      if (categoryOptions) {
        Object.entries(categoryOptions).forEach(([varName, options]) => {
          relevantOptions[varName] = options as string[];
        });
      }
    });

    return relevantOptions;
  }
}